import React, { useMemo, useEffect, useState } from 'react';
import { useSetState } from 'ahooks';
import moment from 'moment';
import { isEmpty, flattenDeep, maxBy } from 'lodash-es';
import { getLocale, useIntl } from 'umi';
import { Layout, Justify, Segment, Row, Col } from 'tea-component';
import ModuleCard from './ModuleCard/index';
import Twitch from '@/pages/intelligence/assets/svg/dataSource/twitch.svg';
import YouKu from '@/pages/intelligence/assets/svg/dataSource/youtube.svg';
import Gaming from '@/pages/intelligence/assets/svg/dataSource/fbgaming-bg.svg';
import RangePicker from '@/pages/home/components/RangePicker';
// import { useStreamingKolRank } from '../hooks';
import { mathFormat } from '@/pages/home/utils/search';
import { TIMETYPETODATE } from '@/pages/dashboard/pages/comparison/_config/constants';
import '@/pages/intelligence/assets/scss/Streaming/kol.scss';
import Loading from '@/pages/intelligence/components/Loading';
import { useOptionslistSegment } from '../hooks/useFilterOptions';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { componentDefault } from '@intelligence/components/Filter/FilterBase/FilterList';
import { fetchStreamingKOLPage } from '@intelligence/services/streaming';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { onFilterConfirm } from '@intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { toDateTypeFilter } from '@/pages/intelligence/utils/common';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

const { Body, Content } = Layout;
type IOptionKey = 'hoursWatched' | 'peackCCV' | 'avgCCV' | 'airtime';

type IMonth = 'daily' | 'weekly' | 'monthly';

export type IOptions = {
  text: string;
  value: IOptionKey;
};
type IStateConfig<S = string, N = number> = {
  metric: IOptionKey;
  start_date: S;
  end_date: S;
  period: IMonth;
  page: N;
  page_size: N;
};

export default function StreamingKol() {
  const { isOperate, filterExtend, setFilterExtend } = useFilterExtend({});
  const getExtendParams = () => {
    const newFetchParams: any = {};
    const timeExtend = filterExtend[filterKeyMap.RANGE_TIME];
    if (timeExtend) {
      newFetchParams.period = timeExtend?.granularity || 'daily';
      newFetchParams.start_date = timeExtend?.start_date || '';
      newFetchParams.end_date = timeExtend?.end_date || '';
    }
    return newFetchParams;
  };

  const extendParams = isOperate ? getExtendParams() : {};
  const [paramsDefault, setParamsDefault] = useSetState<IStateConfig>({
    metric: 'hoursWatched',
    start_date: extendParams.start_date || '',
    end_date: extendParams.end_date || '',
    period: extendParams.period || 'daily',
    page: 1,
    page_size: 10,
  });
  // const debouncedValue = useDebounce(paramsDefault, { wait: 500 });
  // const { data = [], loading } = useStreamingKolRank(
  //   useMemo(() => debouncedValue, [debouncedValue]),
  // );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { formatMessage } = useIntl();
  const [optionslist, hoverBubbleText] = useOptionslistSegment(paramsDefault.metric);
  // useMount(() => {
  //   buriedPointPage(dbiPageLogMes.streamingInfluencer);
  // });
  // tab回调
  const handleOnClickSelectTabKey = <V extends unknown>(value: V) => {
    setParamsDefault({ metric: value as IOptionKey });
  };
  useEffect(() => {
    buriedPointPage(dbiPageLogMes.streamingInfluencer);
  }, []);

  const getPage = async (params: IStateConfig) => {
    setLoading(true);
    try {
      const resData = await fetchStreamingKOLPage(params);
      setData(resData);

      const dateType = toDateTypeFilter[params.period];
      const dateValue = [
        moment(resData?.req?.start_date || ''),
        moment(resData?.req?.end_date || ''),
      ];
      // 第一次也请求继承，因为下钻页传空，后端会返回的时间与榜单不一致
      const filterValue = { [filterKeyMap.RANGE_TIME]: { type: dateType, value: dateValue } };
      const formatValue = onFilterConfirm(filterValue);
      setFilterExtend(formatValue.apiValue);
    } catch (err) {
      setData(null);
    }
    setLoading(false);
  };
  useEffect(() => {
    getPage(paramsDefault);
  }, [JSON.stringify(paramsDefault)]);

  // 日期回调
  const handleOnOnchangePickerTime = (value: { format: (arg0: string) => any }[], type: string) => {
    if (isEmpty(value)) return;
    const period = {
      date: 'daily',
      week: 'weekly',
      month: 'monthly',
    }[type as string];
    setParamsDefault({
      start_date: value?.[0]?.format('YYYY-MM-DD'),
      end_date:
        type === 'month'
          ? value?.[1]?.endOf('month')?.format('YYYY-MM-DD')
          : value?.[1]?.format('YYYY-MM-DD'),
      period: period as IMonth,
    });

    // 保存时间作为下钻继承值
    const filterValue = { [filterKeyMap.RANGE_TIME]: { type, value } };
    const formatValue = onFilterConfirm(filterValue);
    setFilterExtend(formatValue.apiValue);
  };
  // 卡片数据
  const memoCardList = useMemo(() => {
    const titleConfig = ['dbi_twitch_channels', 'dbi_youtube_channels', 'dbi_facebook_channels'];
    const linkConfig = ['Key Metrics', 'YouTube Channels', 'Facebook Channels'];
    const svgConfig = [Twitch, YouKu, Gaming];
    return data?.data?.map((item, index) => {
      const maxValue = maxBy(item.list, (obj) => obj.metric_list[0].value)?.metric_list[0].value;
      const list = item.list.map((task, taskIndex) => ({
        id: taskIndex + 1,
        img: task?.user_info.icon,
        title: task?.user_info.name,
        desc:
          item.platform === 'twitch'
            ? task?.user_info.language
            : item.platform === 'ytg'
            ? task?.user_info.market
            : undefined,
        // TODO:第一项为100%,待处理
        num: taskIndex === 0 ? 100 : (task.metric_list?.[0]?.value / maxValue) * 100,
        value: mathFormat(task.metric_list?.[0]?.value),
        ratio: `${Math.round(task.metric_list?.[0]?.growth_ratio)}`,
        url: task?.user_info?.url,
      }));
      return {
        title: formatMessage({ id: titleConfig[index] }),
        svg: svgConfig[index],
        list: flattenDeep(list),
        link: linkConfig[index],
      };
    });
  }, [data, formatMessage]);

  const timeType = {
    daily: 'Day',
    weekly: 'Week',
    monthly: 'Month',
  }[paramsDefault.period as string];

  return (
    <Layout className="dbi-streaming-kol">
      <Body>
        <Content>
          <Content.Header title={formatMessage({ id: 'dbi_Primary_kol' })} />
          <Content.Body full>
            <MarqueeBanner />
            <Justify
              className="streaming-justify"
              left={
                <div className="streaming-justify-flex">
                  <Segment
                    className="segment-light"
                    value={paramsDefault.metric.toString()}
                    onChange={handleOnClickSelectTabKey}
                    options={optionslist}
                  />
                  <div className="search-item-date">
                    <RangePicker
                      type={TIMETYPETODATE[timeType]}
                      language={getLocale() === 'zh-CN' ? 'cn' : 'en'}
                      value={[
                        !isEmpty(paramsDefault?.start_date)
                          ? moment(paramsDefault?.start_date)
                          : moment(data?.req?.start_date),
                        !isEmpty(paramsDefault?.end_date)
                          ? moment(paramsDefault?.end_date)
                          : moment(data?.req?.end_date),
                      ]}
                      typeList={['date', 'week', 'month']}
                      className="dtb-rangepicker-label-wrap"
                      showSelectType={true}
                      startOfWeek={0}
                      onChange={handleOnOnchangePickerTime}
                      defaultValue={componentDefault}
                    />
                  </div>
                </div>
              }
            />
            {loading && (
              <div style={{ height: '670px' }}>
                <Loading />
              </div>
            )}
            {!loading && !memoCardList?.length && (
              <CardSlotV2 style={{ marginTop: 10 }}>
                <Abnormal isLoading={false} isNoData={true} component={<></>} />
              </CardSlotV2>
            )}
            {!loading && memoCardList?.length > 0 && (
              <Row>
                {memoCardList?.map((e: any, i: any) => (
                  <Col key={i} span={8}>
                    <ModuleCard data={e} {...{ loading, hoverBubbleText }} />
                  </Col>
                ))}
              </Row>
            )}
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
}
